import {useEffect, useState} from "react";
import {NewsItem} from "../types/news";
import {newsService} from "../services/newsService";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {Query} from "appwrite";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";

const DEFAULT_PAGE_SIZE = 5

export const useNewsController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [newsList, setNewsList] = useState<NewsItem[]>([])
  const [totalNewsCount, setTotalNewsCount] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState(() => {
    const page = Number(new URLSearchParams(window.location.search).get("page"))
    return Number.isInteger(page) && page > 0 ? page : 1
  })
  const [searchValue, setSearchValue] = useState(() => new URLSearchParams(window.location.search).get("search") || "")
  const [lastUpdate, setLastUpdate] = useState(0)
  const [selectedNewsItem, setSelectedNewsItem] = useState<NewsItem | null>(null)


  useEffect(() => {
    const queries = [
      Query.orderDesc('$createdAt'),
      Query.limit(DEFAULT_PAGE_SIZE),
      Query.offset((currentPage - 1) * DEFAULT_PAGE_SIZE)
    ]

    if (searchValue?.length) {
      queries.unshift(Query.search('title', searchValue));
    }

    fetchNewsList(queries)
  }, [lastUpdate, currentPage, searchValue])

  const changeLastUpdate = () => {
    setLastUpdate(Date.now())
  }


  const toggleCreateNewsModalOpen = () => {
    const newValue = !isCreateModalOpen
    setIsCreateModalOpen(newValue)
    if (newValue === false) {
      setSelectedNewsItem(null)
    }
  }

  const fetchNewsList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const response = await newsService.list(queries)
      const {newsList, totalCount} = response
      setNewsList(newsList)
      if (totalCount !== totalNewsCount) {
        setTotalNewsCount(totalCount)
      }
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження новин!"});
    } finally {
      setIsLoading(false)
    }
  }

  const createNewsHandler = async (data: NewsItem) => {
    setIsLoading(true)
    try {
      await newsService.createNews(data)
      toggleCreateNewsModalOpen()
      changeLastUpdate()
      openSuccessNotification("Новина успішно створена!")
    } catch (error) {
      showErrorNotification({message: "Помилка створення новини!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const updateNews = async (data: NewsItem) => {
    setIsLoading(true)
    try {
      await newsService.update({$id: selectedNewsItem?.$id, ...data})
      toggleCreateNewsModalOpen()
      changeLastUpdate()
      openSuccessNotification("Новина успішно оновлена!")
    } catch (error) {
      showErrorNotification({message: "Помилка оновлення новини!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const getNewsItemById = async (id: string) => {
    setIsLoading(true)
    try {
      const currentNews = await newsService.getNewsItem(id)
      setSelectedNewsItem(currentNews)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження новини!"});
    } finally {
      setIsLoading(false)
    }
  }

  const deleteNewsHandler = async (id: string) => {
    setIsLoading(true)
    try {
      await newsService.delete(id)
      changeLastUpdate()
      openSuccessNotification("Новина успішно видалена!")
    } catch (error) {
      showErrorNotification({message: "Помилка видалення новини!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const editButtonHandler = (newsItem: NewsItem) => {
    setSelectedNewsItem(newsItem)
    toggleCreateNewsModalOpen()
  }

  const confirmDeleteNews = (id: string) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити новину?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          await deleteNewsHandler(id)
        },
      });

  }

  const onPageChange = (page: number) => {
    setCurrentPage(page)
  }


  return {
    isLoading,
    getNewsItemById,
    isCreateModalOpen,
    setSelectedNewsItem,
    toggleCreateNewsModalOpen,
    createNewsHandler,
    newsList,
    fetchNewsList,
    confirmDeleteNews,
    selectedNewsItem,
    editButtonHandler,
    DEFAULT_PAGE_SIZE,
    totalNewsCount,
    onPageChange,
    setSearchValue,
    searchValue,
    currentPage,
    updateNews
  }
}
export default useNewsController
